import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SubscriptionModal, PaymentRegionModal } from '@pisaka/ui';
import { useSubscription } from './useSubscription';
import { socket } from '../../helpers/socket';
import { fetchCurrentSubscription as fetchCurrentSubscriptionRedux } from './state/controller';
import { AppStateTypes } from '../../store/appStateTypes';
import { AppDispatch } from '../../store/configureStore';
import { axiosInstance } from '../../helpers/axiosInstance';
import { useTranslation } from 'react-i18next';

const PLAN_NAMES_EN: Record<string, string> = {
  'Премиум': 'Premium',
  'Старт': 'Starter',
  'Стандарт': 'Standard',
  'Максимум': 'Maximum',
};

interface SubscriptionContainerProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  context?: 'premium' | 'tokens' | 'all';
}

export const SubscriptionContainer: React.FC<SubscriptionContainerProps> = ({
  isOpen,
  onClose,
  onSuccess,
  context = 'all',
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { token } = useSelector((state: AppStateTypes) => state.auth);

  const {
    plans,
    isLoading,
    error,
    fetchPlans,
    fetchCurrentSubscription,
    purchasePremium,
    clearError,
    currentSubscription,
  } = useSubscription();

  // Состояние модалки выбора региона
  const [regionModalOpen, setRegionModalOpen] = useState(false);
  const [pendingPlanId, setPendingPlanId] = useState<string | null>(null);
  const [pendingIncludePremium, setPendingIncludePremium] = useState(false);
  const [pendingTotalPrice, setPendingTotalPrice] = useState(0);

  useEffect(() => {
    if (isOpen) {
      fetchPlans();
      fetchCurrentSubscription();
    }
  }, [isOpen, fetchPlans, fetchCurrentSubscription]);

  useEffect(() => {
    if (!isOpen) clearError();
  }, [isOpen, clearError]);

  const formatDuration = (days: number): string => {
    if (days === 30 || days === 31) return t('pricing.card.duration.month_1');
    if (days === 90 || days === 92) return t('pricing.card.duration.months_3');
    if (days === 180 || days === 183) return t('pricing.card.duration.months_6');
    if (days === 365 || days === 366) return t('pricing.card.duration.year_1');
    if (days >= 36500) return t('pricing.card.duration.forever');
    return t('pricing.card.duration.days', { count: days });
  };

  const allPlans = plans
    .map((plan) => {
      const isEn = i18n.language === 'en';
      return {
        id: plan.id,
        name: isEn ? (PLAN_NAMES_EN[plan.name] ?? plan.name) : plan.name,
        price: Number(plan.price),
        durationDays: plan.durationDays,
        duration: formatDuration(plan.durationDays),
        features: plan.features || [],
        tokens: plan.tokens || 0,
        isPopular: plan.name === 'Стандарт',
        priceKzt: plan.priceKzt,
      };
    })
    .filter((plan) => {
      if (context === 'premium') return !plan.tokens || plan.tokens === 0;
      if (context === 'tokens') return plan.tokens && plan.tokens > 0;
      return true;
    });

  // Открываем модалку выбора региона вместо сразу запуска виджета
  const handlePlanAction = (planId: string, includePremium: boolean, totalPrice: number) => {
    setPendingPlanId(planId);
    setPendingIncludePremium(includePremium);
    setPendingTotalPrice(totalPrice);
    onClose(); // закрываем модалку планов
    setRegionModalOpen(true);
  };

  // ── YooKassa (Россия) ───────────────────────────────────────────
  const launchYooKassa = async (planId: string, includePremium: boolean, totalPrice: number) => {
    if (!token) return;
    setRegionModalOpen(false);

    const plan = plans.find(p => p.id.toString() === planId);
    const isTokenPlan = plan?.tokens && plan.tokens > 0;

    try {
      let paymentConfirmationToken: string | null = null;
      let paymentId: string | null = null;

      if (isTokenPlan) {
        const response = await axiosInstance(token).post('/api/pricing/purchase', { planId, includePremium, totalPrice });
        paymentConfirmationToken = response.data.paymentConfirmationToken;
        paymentId = response.data.paymentId;
      } else {
        const result = await purchasePremium(planId);
        paymentConfirmationToken = result?.paymentConfirmationToken ?? null;
        paymentId = result?.paymentId ?? null;
        if (result?.status === 'succeeded') { onSuccess?.(); return; }
      }

      if (paymentConfirmationToken && paymentId) {
        if (!socket.connected) socket.connect();

        const handleSuccess = async () => {
          await Promise.all([
            dispatch(fetchCurrentSubscriptionRedux(token)),
            fetchCurrentSubscription(),
          ]);
          onSuccess?.();
          socket.off(`payment:${paymentId}`, handleSuccess);
        };

        socket.on(`payment:${paymentId}`, handleSuccess);

        // @ts-ignore
        const checkout = new window.YooMoneyCheckoutWidget({
          confirmation_token: paymentConfirmationToken,
          customization: { modal: true },
          error_callback() { console.error('YooKassa widget error'); },
        });

        checkout.render();
        checkout.on('success', () => { checkout.destroy(); handleSuccess(); });
        checkout.on('fail', () => { socket.off(`payment:${paymentId}`, handleSuccess); checkout.destroy(); });
      }
    } catch (err) {
      console.error('YooKassa payment failed:', err);
    }
  };

  // ── Forte Bank (Весь мир) — заглушка до подключения ────────────
  const launchForte = async (_planId: string) => {
    setRegionModalOpen(false);
    console.log('Forte Bank payment coming soon');
    // TODO: реализовать после подключения Forte Bank
  };

  const handleSelectRussia = () => {
    if (pendingPlanId) launchYooKassa(pendingPlanId, pendingIncludePremium, pendingTotalPrice);
  };

  const handleSelectWorld = () => {
    if (pendingPlanId) launchForte(pendingPlanId);
  };

  const hasPremium = currentSubscription?.plan?.features?.includes('pdf_export') || false;

  const featureLabels = {
    pdf_export: t('pricing.featureLabels.pdf_export'),
    double_page: t('pricing.featureLabels.double_page'),
    no_ads: t('pricing.featureLabels.no_ads'),
  };

  const planCardTexts = {
    popular:             t('pricing.card.popular'),
    forDuration:         (duration: string) => duration === t('pricing.card.duration.forever') ? duration : t('pricing.card.forDuration', { duration }),
    getDuration:         formatDuration,
    whatsIncluded:       t('pricing.card.whatsIncluded'),
    getTokenDescription: (k: number) => t('pricing.card.tokenDescription', { k }),
    getAddBasic:         (price: number) => t('pricing.card.addBasic', { price }),
    processing:          t('pricing.card.processing'),
    buy:                 t('pricing.card.buy'),
    comingSoon:          t('pricing.card.comingSoon'),
    purchased:           t('pricing.card.purchased'),
    or:                  t('paymentRegion.or'),
    russia:              t('paymentRegion.russia'),
    world:               t('paymentRegion.world'),
  };

  const regionModalTexts = {
    title:          t('paymentRegion.title'),
    russiaTitle:    t('paymentRegion.russiaTitle'),
    russiaSubtitle: t('paymentRegion.russiaSubtitle'),
    worldTitle:     t('paymentRegion.worldTitle'),
    worldSubtitle:  t('paymentRegion.worldSubtitle'),
    comingSoon:     t('pricing.card.comingSoon'),
    or:             t('paymentRegion.or'),
  };

  const pendingPlan = plans.find(p => p.id.toString() === pendingPlanId);
  const pendingPlanName = pendingPlan
    ? (i18n.language === 'en' ? (PLAN_NAMES_EN[pendingPlan.name] ?? pendingPlan.name) : pendingPlan.name)
    : '';

  return (
    <>
      <SubscriptionModal
        isOpen={isOpen}
        onClose={() => { if (!isLoading) onClose(); }}
        onSubscribe={(id) => handlePlanAction(id, false, 0)}
        onTokenPurchase={handlePlanAction}
        plans={allPlans}
        featureLabels={featureLabels}
        hasPremium={hasPremium}
        isLoadingPlans={isLoading}
        texts={{ title: t('subscription.title'), choosePlan: t('subscription.choosePlan') }}
        planCardTexts={planCardTexts}
      />

      <PaymentRegionModal
        isOpen={regionModalOpen}
        onClose={() => setRegionModalOpen(false)}
        onSelectRussia={handleSelectRussia}
        onSelectWorld={handleSelectWorld}
        planName={pendingPlanName}
        priceRub={pendingPlan ? Number(pendingPlan.price) : 0}
        priceKzt={pendingPlan?.priceKzt}
        texts={regionModalTexts}
      />
    </>
  );
};
